import { useState } from 'react';
import type { FormConfig, FormSection, FormField, FormError } from '@/types/patientForm';

interface PatientFormRendererProps {
  config: FormConfig;
  initialData?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void | Promise<void>;
  onCancel?: () => void;
  readOnly?: boolean;
}

export default function PatientFormRenderer({
  config,
  initialData = {},
  onSubmit,
  onCancel,
  readOnly = false,
}: PatientFormRendererProps) {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [errors, setErrors] = useState<FormError[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(
      config.sections
        .filter((s) => s.collapsible && s.collapsed_by_default)
        .map((s) => s.id)
    )
  );
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    // Clear error for this field
    setErrors((prev) => prev.filter((e) => e.field !== fieldId));
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: FormError[] = [];

    config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const value = formData[field.id];

        // Required field validation
        if (field.required && (!value || value === '')) {
          newErrors.push({
            field: field.id,
            message: `${field.label} is required`,
          });
        }

        // Pattern validation
        if (value && field.validation?.pattern) {
          const regex = new RegExp(field.validation.pattern);
          if (!regex.test(value)) {
            newErrors.push({
              field: field.id,
              message: field.validation.message || `Invalid format for ${field.label}`,
            });
          }
        }

        // Min/max validation
        if (value && field.type === 'number') {
          if (field.validation?.min !== undefined && value < field.validation.min) {
            newErrors.push({
              field: field.id,
              message: `${field.label} must be at least ${field.validation.min}`,
            });
          }
          if (field.validation?.max !== undefined && value > field.validation.max) {
            newErrors.push({
              field: field.id,
              message: `${field.label} must be at most ${field.validation.max}`,
            });
          }
        }

        // Length validation
        if (value && typeof value === 'string') {
          if (
            field.validation?.minLength !== undefined &&
            value.length < field.validation.minLength
          ) {
            newErrors.push({
              field: field.id,
              message: `${field.label} must be at least ${field.validation.minLength} characters`,
            });
          }
          if (
            field.validation?.maxLength !== undefined &&
            value.length > field.validation.maxLength
          ) {
            newErrors.push({
              field: field.id,
              message: `${field.label} must be at most ${field.validation.maxLength} characters`,
            });
          }
        }
      });
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.id] ?? field.default_value ?? '';
    const fieldErrors = errors.filter((e) => e.field === field.id);
    const hasError = fieldErrors.length > 0;

    const baseInputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      hasError ? 'border-red-500' : 'border-gray-300'
    } ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`;

    const renderInput = () => {
      switch (field.type) {
        case 'textarea':
          return (
            <textarea
              id={field.id}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              disabled={readOnly}
              rows={field.rows || 3}
              className={baseInputClass}
            />
          );

        case 'select':
          return (
            <select
              id={field.id}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              required={field.required}
              disabled={readOnly}
              className={baseInputClass}
            >
              <option value="">-- Select --</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );

        case 'checkbox':
          return (
            <input
              id={field.id}
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              disabled={readOnly}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
          );

        case 'radio':
          return (
            <div className="space-y-2">
              {field.options?.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    disabled={readOnly}
                    className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="ml-2">{option.label}</span>
                </label>
              ))}
            </div>
          );

        default:
          return (
            <input
              id={field.id}
              type={field.type}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              disabled={readOnly}
              className={baseInputClass}
            />
          );
      }
    };

    return (
      <div key={field.id} className="space-y-1">
        <label htmlFor={field.id} className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {renderInput()}
        {field.help_text && (
          <p className="text-xs text-gray-500">{field.help_text}</p>
        )}
        {fieldErrors.map((error, idx) => (
          <p key={idx} className="text-xs text-red-600">
            {error.message}
          </p>
        ))}
      </div>
    );
  };

  const renderSection = (section: FormSection) => {
    const isCollapsed = collapsedSections.has(section.id);
    const sortedFields = [...section.fields].sort((a, b) => a.order - b.order);

    return (
      <div key={section.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div
          className={`px-6 py-4 bg-gray-50 border-b border-gray-200 ${
            section.collapsible ? 'cursor-pointer hover:bg-gray-100' : ''
          }`}
          onClick={() => section.collapsible && toggleSection(section.id)}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
              {section.description && (
                <p className="text-sm text-gray-600 mt-1">{section.description}</p>
              )}
            </div>
            {section.collapsible && (
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  isCollapsed ? '' : 'rotate-180'
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </div>
        </div>

        {!isCollapsed && (
          <div className="px-6 py-4 space-y-4">
            {sortedFields.map((field) => renderField(field))}
          </div>
        )}
      </div>
    );
  };

  const sortedSections = [...config.sections].sort((a, b) => a.order - b.order);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {sortedSections.map((section) => renderSection(section))}

      {!readOnly && (
        <div className="flex items-center gap-3 pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </form>
  );
}
